import { useMemo, useState } from "react";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import {
  IUserFormSubmission,
  IUserFormSubmissionListItem,
} from "../models/userFormSubmissionModels";

interface IUseUserFormsDialogProps {
  userId?: number;
  visible: boolean;
}

export interface IUserFormRow {
  id: number;
  formName: string;
  filledOnText: string;
  /** "Пилатес · 12/10/2026, 18:00", or "Без клас" for a form sent by email. */
  eventText: string;
}

// The API sends UTC without a zone designator; read it as UTC, not as local time.
const parseUtc = (iso: string) =>
  new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);

/**
 * The client's "Форми": every form they filled in this studio, newest first, and the one
 * opened for reading. Loads only while the dialog is open.
 */
export const useUserFormsDialog = ({ userId, visible }: IUseUserFormsDialogProps) => {
  const [openedId, setOpenedId] = useState<number | null>(null);

  const { data, isLoading } = useApiQuery<IUserFormSubmissionListItem[]>({
    queryKey: [endpointsMetadata.clientForm.getUserSubmissions, String(userId)],
    endpoint: `${endpointsMetadata.clientForm.getUserSubmissions}?userId=${userId}`,
    enabled: visible && !!userId,
    globalIsLoading: false,
  });

  const { data: opened } = useApiQuery<IUserFormSubmission>({
    queryKey: [endpointsMetadata.clientForm.getSubmissionContent, String(openedId)],
    endpoint: `${endpointsMetadata.clientForm.getSubmissionContent}?id=${openedId}`,
    enabled: openedId !== null,
    globalIsLoading: false,
  });

  const rows: IUserFormRow[] = useMemo(
    () =>
      (data ?? []).map((item) => ({
        id: item.id,
        formName: item.formName,
        filledOnText: dateUtility.formatDateTime(parseUtc(item.createdOn)),
        eventText: item.eventName
          ? [
              item.eventName,
              item.eventDate ? dateUtility.formatDateTime(parseUtc(item.eventDate)) : "",
            ]
              .filter(Boolean)
              .join(" · ")
          : "Без клас",
      })),
    [data],
  );

  return {
    rows,
    isLoading,
    openedHtml: opened?.renderedHtml ?? "",
    openedFormName: opened?.formName ?? "",
    isOpenedVisible: openedId !== null,
    openForm: (id: number) => setOpenedId(id),
    closeForm: () => setOpenedId(null),
  };
};
